import type { Translate } from 'next-translate';
import { usePathname } from 'next/navigation';
import React from 'react';
import { Container } from '@/components/elements/Container';
import type { LandingConfigItem } from '@/types';
import ArrowIcon from '@/public/icons/arrow-left.svg';
import { CardItem } from './CardItem';
import styles from './ToolsWidget.module.scss';

interface ToolsWidgetProps {
	data: LandingConfigItem['explore'];
	t: Translate;
}
export const ToolsWidget: React.FC<ToolsWidgetProps> = ({ data, t }) => {
	const pathname = usePathname();
	const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
	const containerRef = React.useRef<HTMLDivElement | null>(null);

	// States
	const [cardWidth, setCardWidth] = React.useState(0);
	const [canScroll, setCanScroll] = React.useState({
		left: false,
		right: false,
	});

	// Actions
	const handleScroll = (direction: 'left' | 'right') => {
		const el = scrollContainerRef.current;
		if (!el) return;

		const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;
		el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
	};

	// Effects
	React.useEffect(() => {
		if (typeof window === 'undefined') return;
		const updateDistance = () => {
			const distance = containerRef.current?.getBoundingClientRect().left ?? 0;
			document.documentElement.style.setProperty('--distance', `${distance}px`);
		};

		updateDistance();

		window.addEventListener('resize', updateDistance);
		return () => window.removeEventListener('resize', updateDistance);
	}, []);

	React.useEffect(() => {
		if (typeof window === 'undefined') return;
		const getCardWidth = () => {
			if (window.innerWidth >= 1024) return 356 + 40;
			if (window.innerWidth >= 768) return 288 + 24;
			return 153 + 16;
		};
		setCardWidth(getCardWidth());
	}, []);

	React.useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const updateScroll = () => {
			setCanScroll({
				left: container.scrollLeft > 0,
				right:
					container.scrollLeft + container.clientWidth <
					container.scrollWidth - 1,
			});
		};

		updateScroll();

		container.addEventListener('scroll', updateScroll);
		return () => container.removeEventListener('scroll', updateScroll);
	}, []);
	const sortedFilteredList = React.useMemo(() => {
		if (!data?.list) return [];
		// show newer cards - first
		return data.list
			.filter(item => item.href && !pathname.startsWith(item.href))
			.sort((a, b) => b.id - a.id);
	}, [data?.list, pathname]);

	if (!data) return null;

	return (
		<section className={styles['slider']} aria-labelledby='slider-title'>
			<Container className={styles['slider__container-top']} ref={containerRef}>
				<h2 className={styles['slider__title']}>
					{t('exploreData.mainTitle')}
				</h2>

				<div className={styles['slider__controls']}>
					<button
						type='button'
						onClick={() => handleScroll('left')}
						disabled={!canScroll.left}
						aria-label={t('buttons.previousSlide') || 'Previous slide'}
						className={styles['slider__navBtn']}
					>
						<ArrowIcon
							className={`${styles['slider__left']} ${
								!canScroll.left ? styles['slider__left_disabled'] : ''
							}`}
							aria-hidden='true'
							focusable='false'
						/>
					</button>
					<button
						type='button'
						onClick={() => handleScroll('right')}
						disabled={!canScroll.right}
						aria-label={t('buttons.nextSlide') || 'Next slide'}
						className={styles['slider__navBtn']}
					>
						<ArrowIcon
							className={`${styles['slider__right']}`}
							aria-hidden='true'
							focusable='false'
						/>
					</button>
				</div>
			</Container>

			<div
				className={styles['slider__scroll-container']}
				ref={scrollContainerRef}
			>
				{sortedFilteredList.map(slide => (
					<CardItem slide={slide} t={t} key={slide.id} />
				))}
			</div>

			{/* <div className={`${styles['slider__button-wrapper']}`}> */}
			{/*	<Button */}
			{/*		as='link' */}
			{/*		href={data.hrefToolsPage} */}
			{/*		rel='noopener noreferrer' */}
			{/*		aria-label={data.title} */}
			{/*		preset='landing' */}
			{/*		text={t(data.buttonText)} */}
			{/*		size='large' */}
			{/*		classes={{ */}
			{/*			button: styles['slider__button'], */}
			{/*		}} */}
			{/*	/> */}
			{/* </div> */}
		</section>
	);
};
